import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Worker is bundled from the local pdfjs-dist dependency, not fetched from a CDN
// at runtime: webpack 5 rewrites `new URL(..., import.meta.url)` into an emitted
// asset, so the worker is served same-origin and its version cannot drift from
// the installed library's.
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

export function applyWatermark(ctx, canvas, watermark) {
  const { name, id, timestamp } = watermark;
  const line1 = name || 'Student';
  const line2 = `${(id || '').slice(0, 8)} · ${timestamp}`;
  const fontSize = Math.max(13, Math.floor(canvas.width / 30));
  const lineGap = Math.floor(fontSize * 1.5);
  const repeatStep = fontSize * 9;
  const diag = Math.hypot(canvas.width, canvas.height);

  ctx.save();
  ctx.globalAlpha = 0.14;
  ctx.fillStyle = '#1e293b';
  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(-Math.PI / 6);
  for (let y = -diag; y <= diag; y += repeatStep) {
    ctx.fillText(line1, 0, y);
    ctx.fillText(line2, 0, y + lineGap);
  }
  ctx.restore();
}

// Memoized: re-rendering a page means re-rasterising the canvas, which is both
// expensive and visible (the page blanks for a frame). Only a genuinely new
// document / page number / watermark should cause that.
const PdfPage = React.memo(function PdfPage({ pdfDoc, pageNum, watermark }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let cancelled = false;

    pdfDoc.getPage(pageNum).then((page) => {
      if (cancelled || !canvasRef.current) return;
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = canvasRef.current;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      return page.render({ canvasContext: ctx, viewport }).promise.then(() => {
        if (!cancelled && watermark) applyWatermark(ctx, canvas, watermark);
      });
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [pdfDoc, pageNum, watermark]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full block shadow-sm border border-slate-200 dark:border-slate-700 rounded"
      style={{ userSelect: 'none' }}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
});

function PdfExamViewer({ src, title = 'PDF Document', watermark = null, className = '' }) {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const taskRef = useRef(null);

  useEffect(() => {
    if (!src) return;
    setLoading(true);
    setError(null);
    setPdfDoc(null);
    setPageCount(0);

    const task = pdfjsLib.getDocument(src);
    taskRef.current = task;

    task.promise
      .then((pdf) => {
        setPdfDoc(pdf);
        setPageCount(pdf.numPages);
        setLoading(false);
      })
      .catch((err) => {
        if (err?.name !== 'AbortException') {
          setError('Failed to load PDF.');
          setLoading(false);
        }
      });

    return () => {
      taskRef.current?.destroy().catch(() => {});
    };
  }, [src]);

  if (!src) {
    return (
      <div className={`flex items-center justify-center text-slate-500 ${className}`}>
        No PDF available.
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-16 ${className}`}>
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center py-16 text-red-500 text-sm ${className}`}>
        {error}
      </div>
    );
  }

  return (
    <div className={`space-y-2 overflow-y-auto ${className}`}>
      {Array.from({ length: pageCount }, (_, i) => (
        <PdfPage key={i + 1} pdfDoc={pdfDoc} pageNum={i + 1} watermark={watermark} />
      ))}
    </div>
  );
}

// Callers pass a memoized `watermark`; with that, a parent re-render (e.g. the
// exam window's 60s server resync) no longer reaches the canvas at all.
export default React.memo(PdfExamViewer);
